import { useTranslation } from "react-i18next";
import { Input } from "@/uikit";
import { FixButton } from "@/elements-react/fix-button";
import classNames from "classnames";
import style from "./form.module.scss";
import hashIcon from "@/assets/form/hash.svg";
import letterIcon from "@/assets/form/letter.svg";
import accountIcon from "@/assets/form/account.svg";
import mailIcon from "@/assets/form/mail.svg";
import phoneIcon from "@/assets/form/phone.svg";
import { Icon } from "@/elements-react/fix-icon";
import { useFormFindPartCommon } from "@/elements-react/form-find-part/FormCommon";
import { FixUpload, FixUploadRef } from "@/elements-react/fix-upload";
import { Templates, useTemplate } from "@/util-react/template";
import React from "preact/compat";
import { useRef } from "preact/hooks";
import { FormSuccessState } from "@/elements-react/form-success-state";

export const FormFindPart = () => {
  const { t } = useTranslation();
  const { formData, formErrors, isLoading, changeHandler, handleFileUpload, handleSubmit, isSuccess, setFile, setIsSuccess, serverError } = useFormFindPartCommon();
  const uploadRef = useRef<FixUploadRef>(null);

  const handleSubmitForm = () => {
    handleSubmit();
    if (uploadRef.current) {
      uploadRef.current.clear();
    }
  };

  return (
    <div className={classNames(style.mobile, style.container)} id={'find-form'}>
      <div className={classNames(style.leftContainer)}>
        <div className={classNames(style.textContainer)}>
          <div>
            <h2 className={classNames(style.title)}>{t("form.findPart.title", "Can't find a spare part?")}</h2>
          </div>
          <div className={classNames(style.description)}>
            {t("form.findPart.description", "Enter your details, and we’ll find the best spare part options for you - quickly & at the best price")}
          </div>
        </div>
      </div>
      <div className={classNames(style.rightContainer)}>
        {!isSuccess && (
          <form className={classNames(style.formGroup)}>
            <div className={classNames(style.fieldsGroup)}>
              <div className={classNames(style.fieldsGroup2)}>
                <Input
                  name="vin"
                  type="text"
                  placeholder="WDB9066331S123456"
                  value={formData.vin}
                  onChange={changeHandler}
                  customError={formErrors.vin}
                  icon={<Icon width={16} src={hashIcon} />}
                  iconPosition="prefix"
                />

                <Input
                  name="mpn"
                  type="text"
                  placeholder={t("form.findPart.placeholder.mpn", "Brake Pads, A123456789")}
                  value={formData.mpn}
                  onChange={changeHandler}
                  customError={formErrors.mpn}
                  icon={<Icon width={16} src={letterIcon} />}
                  iconPosition="prefix"
                />
              </div>
              <FixUpload ref={uploadRef} onUpload={handleFileUpload} onReset={() => setFile(null)} />
              <div className={classNames(style.fieldsGroup2)}>
                <Input
                  name="companyName"
                  type="text"
                  placeholder={t("form.findPart.placeholder.companyName", "Your name/Company name")}
                  value={formData.companyName}
                  onChange={changeHandler}
                  customError={formErrors.companyName}
                  required
                  icon={<Icon width={16} src={accountIcon} />}
                  iconPosition="prefix"
                />

                <Input
                  name="email"
                  type="email"
                  placeholder={t("form.findPart.placeholder.email", "Email")}
                  value={formData.email}
                  onChange={changeHandler}
                  customError={formErrors.email}
                  required
                  icon={<Icon width={16} src={mailIcon} />}
                  iconPosition="prefix"
                />

                <Input
                  name="phone"
                  type="tel"
                  placeholder={t("form.findPart.placeholder.phone", "Phone number")}
                  value={formData.phone}
                  onChange={changeHandler}
                  customError={formErrors.phone}
                  required
                  icon={<Icon width={16} src={phoneIcon} />}
                  iconPosition="prefix"
                />
              </div>
            </div>

            {serverError && <p className={style.serverError} role="alert">{serverError}</p>}

            <FixButton type="submit" variant="primary" size="large" onClick={handleSubmitForm} disabled={isLoading} loading={isLoading} fullWidth>
              {t("form.findPart.submit", "Send")}
            </FixButton>
          </form>
        )}
        {isSuccess && <FormSuccessState onClick={() => setIsSuccess(false)} />}
      </div>
    </div>
  );
};

const templates: Record<Templates, React.ComponentType | null> = {
  [Templates.DEFAULT]: null,
  [Templates.BLACK_AND_BLUE]: FormFindPart,
};

export const FormFindPartMobile = () => {
  const template = useTemplate();

  const TemplatedComponent = templates[template.template ?? Templates.DEFAULT];

  return TemplatedComponent ? <TemplatedComponent /> : null;
};
